(ns machtfit.human
  (:require [cljs-time.core :refer [before? date-time year month day day-of-week today in-days interval]]
            [clojure.string :as str]
            [goog.string :as gstring]
            [machtfit.holidays :refer [school-holidays fixed-holidays one-time-holidays moveable-holidays easter-dates]]
            [machtfit.translation :refer [t]]
            [medley.core :refer [map-vals]]))

(defn- extract-datetime-numbers
  "From a human-entered string, extract the positive integers they wrote, interpreting non-numerals
  as separators between numbers.

  We extract like this:
  9 -> 9
  09 -> 09
  930 -> 9 30 ; this is one of two cases where we split off only one digit from the beginning
  0930 -> 09 30
  41116 -> 4 11 16 ; this is the other case where we split off only one digit from the beginning
  240516 -> 24 05 16
  2405245 -> 24 05 245
  24052016 -> 24 05 2016
  4 5 16 -> 4 5 16
  0405 16 -> 04 05 16
  "
  [s]
  (let [maybe-split-first (fn [numbers]
                            (let [fst (first numbers)
                                  fst-length (count fst)]
                              (cond
                                (< fst-length 3)
                                numbers

                                (contains? #{3 5 7} fst-length)
                                (concat [(subs fst 0 1)
                                         (subs fst 1)]
                                        (rest numbers))

                                :else
                                (concat [(subs fst 0 2)
                                         (subs fst 2)]
                                        (rest numbers)))))
        maybe-split-second (fn [numbers]
                             (concat [(first numbers)]
                                     (maybe-split-first (rest numbers))))]
    (mapv js/parseInt
          (some-> s
                  str/trim
                  (str/replace #"[^0-9]" " ")
                  (str/split #" +")
                  maybe-split-first
                  maybe-split-second))))

(defn human->time
  "Convert a human-entered string into a time value."
  [human]
  (let [[raw-hour raw-minute] (extract-datetime-numbers human)
        sane-hour (cond
                    (nil? raw-hour) nil
                    (<= 0 raw-hour 23) raw-hour
                    :else nil)
        sane-minute (cond
                      (nil? raw-minute) 0
                      (<= 0 raw-minute 59) raw-minute
                      :else nil)]
    (cond
      (nil? sane-hour) nil
      (nil? sane-minute) nil
      :else {:hour sane-hour :minute sane-minute})))

(defn timeformat
  "Format a map with hour and minute values into human readable string"
  [{:keys [hour minute]}]
  (gstring/format "%02d:%02d" hour minute))

(defn past? [date]
  (let [today* (today)]
    (when-not (nil? date)
      (before?
       ;; both dates are converted to fresh date objects with the same
       ;; constructor to avoid timezone issues and second offsets, which
       ;; unfortunately are included in "today" calls and "today-at"
       ;; yields a UTC date/time object, again foiling the comparison
       (date-time (year date) (month date) (day date))
       (date-time (year today*) (month today*) (day today*))))))

(defn- guess-year [#_:clj-kondo/ignore day
                   #_:clj-kondo/ignore month]
  (let [this-year (year (today))]
    (cond
      (some nil? [day month]) this-year
      (past? (date-time this-year month day)) (inc this-year)
      :else this-year)))

(defn human->date [human]
  (let [[raw-day raw-month raw-year] (extract-datetime-numbers human)
        sane-day (cond
                   (nil? raw-day) nil
                   (<= 1 raw-day 31) raw-day
                   :else nil)
        sane-month (cond
                     (nil? raw-month) nil
                     (<= 1 raw-month 12) raw-month
                     :else nil)
        guessed-year (guess-year sane-day sane-month)
        sane-year (cond
                    (nil? raw-year) guessed-year
                    (<= 0 raw-year 99) (+ 2000 raw-year)
                    (<= 100 raw-year 9999) raw-year
                    :else guessed-year)]

    #_(println {:raw-day raw-day
                :raw-month raw-month
                :raw-year raw-year
                :sane-day sane-day
                :sane-month sane-month
                :sane-year sane-year})
    (cond
      (nil? sane-day) nil
      (nil? sane-month) nil
      :else [sane-year sane-month sane-day])))

(defn- months [#_:clj-kondo/ignore month]
  (get {1 {:de "Januar"
           :en "January"}
        2 {:de "Februar"
           :en "February"}
        3 {:de "März"
           :en "March"}
        4 {:de "April"
           :en "April"}
        5 {:de "Mai"
           :en "May"}
        6 {:de "Juni"
           :en "June"}
        7 {:de "Juli"
           :en "July"}
        8 {:de "August"
           :en "August"}
        9 {:de "September"
           :en "September"}
        10 {:de "Oktober"
            :en "October"}
        11 {:de "November"
            :en "November"}
        12 {:de "Dezember"
            :en "December"}}
       month))

(defn month-name [date]
  (when date
    (months (month date))))

(defn dateformat-long
  "Format a date value for German human consumption. The month is written-out ('Oktober')"
  [date]
  (when date (gstring/format "%d. %s %04d" (day date) (:de (month-name date)) (year date))))

(def weekdays
  {1 {:keyword :mon
      :short {:de "Mo"
              :en "Mon"}
      :long {:de "Montag"
             :en "Monday"}}
   2 {:keyword :tue
      :short {:de "Di"
              :en "Tue"}
      :long {:de "Dienstag"
             :en "Tuesday"}}
   3 {:keyword :wed
      :short {:de "Mi"
              :en "Wed"}
      :long {:de "Mittwoch"
             :en "Wednesday"}}
   4 {:keyword :thu
      :short {:de "Do"
              :en "Thu"}
      :long {:de "Donnerstag"
             :en "Thursday"}}
   5 {:keyword :fri
      :short {:de "Fr"
              :en "Fri"}
      :long {:de "Freitag"
             :en "Friday"}}
   6 {:keyword :sat
      :short {:de "Sa"
              :en "Sat"}
      :long {:de "Samstag"
             :en "Saturday"}}
   7 {:keyword :sun
      :short {:de "So"
              :en "Sun"}
      :long {:de "Sonntag"
             :en "Sunday"}}})

(defn weekday
  [weekday-int]
  (get weekdays weekday-int))

(defn weekday-short [date]
  (some-> date
          day-of-week
          weekday
          :short
          t))

(defn- to-utc [date]
  (date-time (year date) (month date) (day date)))

(defn days-from-to [from to]
  ;; cljs-time has some issues with daylight saving times when it comes to intervals,
  ;; see https://github.com/andrewmcveigh/cljs-time/issues/133
  ;; Using UTC avoids this problem.
  (let [from (to-utc from)
        to (to-utc to)]
    (if (<= from to)
      (in-days (interval from to))
      (- (in-days (interval to from))))))

(defn- school-holidays-for-year [date #_:clj-kondo/ignore year]
  (let [holidays-that-year (school-holidays year)]
    (->>
      (for [[state holidays] holidays-that-year]
        (for [[holiday-name holiday-ranges] holidays]
          (for [holiday-range holiday-ranges
                :let [single-day-range? (= 2 (count holiday-range))
                      normalized-range (if single-day-range?
                                         (concat holiday-range holiday-range)
                                         holiday-range)
                      [from-day from-month to-day to-month] normalized-range
                      reaches-next-year? (> from-month to-month)
                      from-date (date-time year from-month from-day)
                      to-date (date-time (if reaches-next-year? (inc year) year) to-month to-day)]
                :when (<= from-date date to-date)]
            [(str holiday-name " " year (when reaches-next-year? (str "/" (inc year))))
             [state (if single-day-range?
                      (gstring/format "%02d.%02d." from-day from-month)
                      (gstring/format "%02d.%02d. - %02d.%02d." from-day from-month to-day to-month))]])))
     ;; get rid of the triple nesting
      (apply concat)
      (apply concat)
      (group-by first)
      (map-vals #(->> % (map second) vec)))))

(defn- school-holidays-on-date [date]
  (merge-with
   concat
   ;; We need to check last year as well, because the christmas holidays
   ;; reach into the next year.
   (school-holidays-for-year date (dec (year date)))
   (school-holidays-for-year date (year date))))

(defn holidays [date]
  (let [fixed-holiday (fixed-holidays {:month (month date) :day (day date)})
        one-time-holiday (one-time-holidays {:year (year date) :month (month date) :day (day date)})
        easter-date (easter-dates (year date))
        easter (date-time (year date) (:month easter-date) (:day easter-date))
        days-from-easter (days-from-to easter date)
        moveable-holiday (moveable-holidays days-from-easter)]
    [(into [] (remove nil? [fixed-holiday one-time-holiday moveable-holiday]))
     (school-holidays-on-date (to-utc date))]))

(defn human->amount [amount]
  (let [join-all-butlast (fn [numbers]
                           (if (> (count numbers) 2)
                             [(-> numbers butlast str/join)
                              (-> numbers last)]
                             numbers))
        beef-up-cents (fn [numbers]
                        (if (= 2 (count numbers))
                          [(get numbers 0)
                           (-> (get numbers 1)
                               (str "0")
                               (subs 0 2))]
                          numbers))
        amounts (mapv js/parseInt
                      (some-> amount
                              str
                              (str/replace #"[^0-9,.]+" "")
                              (str/split #"[,.]+")
                              join-all-butlast
                              beef-up-cents))
        [euro cent] amounts
        sane-euro (when-not (js/isNaN euro) euro)
        sane-cent cent]
    (if (or sane-euro sane-cent)
      {:euro (or sane-euro (when (some? sane-cent) 0))
       :cent (or sane-cent (when (some? sane-euro) 0))}
      nil)))

(defn amountformat [amount]
  (when amount
    (let [{:keys [euro cent]} amount]
      (if (> cent 0)
        (str euro "," (gstring/format "%02d" cent))
        (str euro)))))

(defn amount->cents [{:keys [euro cent]}]
  (+ cent (* euro 100)))

(defn describe-date [date & {:keys [reference-date]}]
  (let [reference-date (or reference-date (today))]
    (case (days-from-to reference-date date)
      0 "heute"
      1 "morgen"
      2 "übermorgen"
      -1 "gestern"
      -2 "vorgestern"
      nil)))

(defn number->word
  "For numbers 1-9, return the number spelled out. Otherwise, return the number."
  [number]
  (case number
    1 (t {:de "eins" :en "one"})
    2 (t {:de "zwei" :en "two"})
    3 (t {:de "drei" :en "three"})
    4 (t {:de "vier" :en "four"})
    5 (t {:de "fünf" :en "five"})
    6 (t {:de "sechs" :en "six"})
    7 (t {:de "sieben" :en "seven"})
    8 (t {:de "acht" :en "eight"})
    9 (t {:de "neun" :en "nine"})
    number))
